import { useCallback, useState } from 'react';
import { App } from 'antd';
import * as API from '../services/api';
import { triggerStarterKitZipDownload } from './downloadStarterKit';
import {
  buildEntitlementsByProjectId,
  getProjectKitState,
  getStarterKitIconTooltip,
  getStarterKitIconVisualState,
  KIT_STATE_ACTIVE,
  KIT_STATE_NOT_ISSUED,
  KIT_STATE_PENDING,
} from './starterKitStatus';

function mergeEntitlementWithProject(apiData, project) {
  const projectName = apiData?.project_name
    || project?.name
    || project?.project_id
    || 'Selected project';
  return {
    ...apiData,
    project_name: projectName,
  };
}

async function extractApiDetail(error, fallback) {
  const { sanitizeErrorMessage } = await import('../utils/sanitizeErrorMessage');
  let detail = error?.response?.data?.detail;
  if (detail == null && error?.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      const parsed = JSON.parse(text);
      detail = parsed.detail;
    } catch {
      detail = fallback;
    }
  }
  return sanitizeErrorMessage(detail || error?.message || fallback, { fallback });
}

const REGENERATE_CONFIRM = {
  title: 'Do you want to regenerate Starter Kit?',
  content:
    'This revokes your previous activation token and invalidates the previous Kit. '
    + 'You will receive a new code and must download a new Kit.',
  okText: 'Yes',
  cancelText: 'No',
};

export function useStarterKitIssuance(token) {
  const { message, modal } = App.useApp();
  const [modalOpen, setModalOpen] = useState(false);
  const [entitlement, setEntitlement] = useState(null);
  const [isRegenerateFlow, setIsRegenerateFlow] = useState(false);
  const [creatingProjectId, setCreatingProjectId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [entitlementsByProjectId, setEntitlementsByProjectId] = useState({});

  const refreshEntitlements = useCallback(async () => {
    if (!token) {
      setEntitlementsByProjectId({});
      return;
    }
    try {
      const response = await API.listStarterKitEntitlements(token);
      const rows = Array.isArray(response.data?.entitlements)
        ? response.data.entitlements
        : [];
      setEntitlementsByProjectId(buildEntitlementsByProjectId(rows));
    } catch (error) {
      console.error('Failed to load starter kit entitlements:', error);
    }
  }, [token]);

  const resolveKitState = useCallback(
    (projectId) => getProjectKitState(entitlementsByProjectId, projectId),
    [entitlementsByProjectId],
  );

  const resolveIconVisualState = useCallback(
    (projectId) => getStarterKitIconVisualState(entitlementsByProjectId, projectId),
    [entitlementsByProjectId],
  );

  const resolveIconTooltip = useCallback(
    (projectId) => getStarterKitIconTooltip(entitlementsByProjectId, projectId),
    [entitlementsByProjectId],
  );

  const runIssuance = useCallback(async (project) => {
    const kitState = getProjectKitState(entitlementsByProjectId, project.project_id);
    const isFirstIssue = kitState === KIT_STATE_NOT_ISSUED;
    const response = isFirstIssue
      ? await API.createStarterKitEntitlement(token, project.project_id)
      : await API.regenerateStarterKitEntitlement(token, project.project_id);

    setEntitlement(mergeEntitlementWithProject(response.data, project));
    setIsRegenerateFlow(!isFirstIssue || Boolean(response.data?.regenerated));
    setModalOpen(true);
    await refreshEntitlements();
  }, [entitlementsByProjectId, refreshEntitlements, token]);

  const closeModal = useCallback(async () => {
    if (downloading) return;
    const pendingEntitlement = entitlement;
    const shouldRevoke = pendingEntitlement?.entitlement_id
      && pendingEntitlement?.status === 'pending'
      && !pendingEntitlement?.downloaded_at;

    setModalOpen(false);
    setEntitlement(null);
    setIsRegenerateFlow(false);

    if (!token || !shouldRevoke) {
      await refreshEntitlements();
      return;
    }
    try {
      await API.revokeStarterKitEntitlement(token, pendingEntitlement.entitlement_id);
    } catch (_) {
      // Best effort cleanup only.
    }
    await refreshEntitlements();
  }, [downloading, entitlement, refreshEntitlements, token]);

  const startIssuance = useCallback(async (project) => {
    if (!token || !project?.project_id) return;

    const kitState = getProjectKitState(entitlementsByProjectId, project.project_id);
    const needsConfirm = kitState === KIT_STATE_PENDING || kitState === KIT_STATE_ACTIVE;

    const execute = async () => {
      setCreatingProjectId(project.project_id);
      try {
        await runIssuance(project);
      } catch (error) {
        message.error(await extractApiDetail(error, 'Failed to prepare starter kit'));
      } finally {
        setCreatingProjectId(null);
      }
    };

    if (needsConfirm) {
      modal.confirm({
        ...REGENERATE_CONFIRM,
        onOk: execute,
      });
      return;
    }
    await execute();
  }, [entitlementsByProjectId, message, modal, runIssuance, token]);

  const downloadIssuedKit = useCallback(async () => {
    if (!token || !entitlement?.entitlement_id) return;
    setDownloading(true);
    try {
      const response = await API.downloadStarterKitEntitlement(token, entitlement.entitlement_id);
      triggerStarterKitZipDownload(response.data);
      message.success('Chat starter kit ZIP downloaded');
      setModalOpen(false);
      setEntitlement(null);
      setIsRegenerateFlow(false);
      await refreshEntitlements();
    } catch (error) {
      message.error(await extractApiDetail(error, 'Download failed'));
    } finally {
      setDownloading(false);
    }
  }, [entitlement, message, refreshEntitlements, token]);

  return {
    modalOpen,
    entitlement,
    isRegenerateFlow,
    creatingProjectId,
    downloading,
    refreshEntitlements,
    getProjectKitState: resolveKitState,
    getProjectKitIconVisualState: resolveIconVisualState,
    getStarterKitIconTooltip: resolveIconTooltip,
    closeModal,
    downloadIssuedKit,
    startIssuance,
  };
}
